/* eslint-disable jsx-a11y/no-static-element-interactions */
import ONBOARD_IMG from "@assets/quick-news/Onboard.png";
import {
  loadLottieAnimation,
  type LottieConfig,
  type LottieInstance,
} from "@helper/lottieHelper";
import { formatDate } from "@helper/useDateFormat";
import { useTablaCustomEventAnalyticsCall } from "@hooks/useAnalyticsCall/useTablaCustomEventAnalyticsCall";
import { getAuthorsName } from "@routes/Article/ArticleMetaData";
import QuickNewsMetaData from "@routes/QuickNews/QuickNewsMetaData";
import { setMetaInfo } from "@src/app/routes/QuickNews/helper/QuickNewsHelper";
import type { ProcessedArticleData } from "@transformer/useOsResponse";
import Cookies from "js-cookie";
import {
  type ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { twMerge } from "tailwind-merge";

import QnCardNav from "./QnCardNav";

// Constants
const ANIMATION_DURATION = 300;
const ENTRANCE_DELAY = 100;
const DRAG_THRESHOLD = 50;
const DRAG_UP_THRESHOLD = -30;
const LOTTIE_LOAD_DELAY = 200;
const CANVAS_HEIGHT_MOBILE = 380;
const CANVAS_HEIGHT_DESKTOP = 600;
const CANVAS_WIDTH_MOBILE = 280;
const CANVAS_WIDTH_DESKTOP = 400;
const CANVAS_BREAKPOINT = 500;
const ONBOARD_COOKIE_NAME = "sph_qn_onboarded";
const ONBOARD_VIRTUAL_URL = "/quick-news";
const KEYBOARD_CLOSE_KEYS = new Set(["Escape", "Enter", "ArrowDown", " "]);
const NOOP = () => {};

// Types
interface DragPosition {
  x: number;
  y: number;
}

interface UseDragGestureReturn {
  handleTouchStart: (e: React.TouchEvent) => void;
  handleTouchEnd: (e: React.TouchEvent) => void;
  handleMouseDown: (e: React.MouseEvent) => void;
  handleMouseUp: (e: React.MouseEvent) => void;
}

interface QnOnboardProps {
  isMobile?: boolean;
  initialId?: string;
  setPushState: () => void;
  article: ProcessedArticleData | undefined;
}

// Custom hook for drag gesture handling
const useDragGesture = (onSwipeUp: () => void): UseDragGestureReturn => {
  const dragStartRef = useRef<DragPosition | null>(null);

  const handleDragStart = useCallback((clientX: number, clientY: number) => {
    dragStartRef.current = { x: clientX, y: clientY };
  }, []);

  const handleDragEnd = useCallback(
    (clientX: number, clientY: number) => {
      if (!dragStartRef.current) return;

      const deltaX = clientX - dragStartRef.current.x;
      const deltaY = clientY - dragStartRef.current.y;
      const distance = Math.hypot(deltaX, deltaY);

      // Check if it's a swipe up gesture
      if (distance > DRAG_THRESHOLD && deltaY < DRAG_UP_THRESHOLD) {
        onSwipeUp();
      }

      dragStartRef.current = null;
    },
    [onSwipeUp],
  );

  const handleTouchStart = useCallback(
    (e: React.TouchEvent) => {
      const touch = e.touches[0];
      handleDragStart(touch.clientX, touch.clientY);
    },
    [handleDragStart],
  );

  const handleTouchEnd = useCallback(
    (e: React.TouchEvent) => {
      const touch = e.changedTouches[0];
      handleDragEnd(touch.clientX, touch.clientY);
    },
    [handleDragEnd],
  );

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      handleDragStart(e.clientX, e.clientY);
    },
    [handleDragStart],
  );

  const handleMouseUp = useCallback(
    (e: React.MouseEvent) => {
      handleDragEnd(e.clientX, e.clientY);
    },
    [handleDragEnd],
  );

  // Memoize the return object to prevent unnecessary re-renders
  return useMemo(
    () => ({
      handleTouchStart,
      handleTouchEnd,
      handleMouseDown,
      handleMouseUp,
    }),
    [handleTouchStart, handleTouchEnd, handleMouseDown, handleMouseUp],
  );
};

export default function QnOnboard({
  isMobile = false,
  initialId,
  setPushState,
  article,
}: QnOnboardProps): ReactElement | null {
  const [onBoarded, setOnBoarded] = useState(Boolean(initialId));
  const [isAnimatingOut, setIsAnimatingOut] = useState(false);
  const [isAnimatingIn, setIsAnimatingIn] = useState(true);
  const [mainLottieLoaded, setMainLottieLoaded] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const readMoreCanvasRef = useRef<HTMLCanvasElement>(null);
  const mainLottieInstanceRef = useRef<LottieInstance | null>(null);
  const readMoreLottieInstanceRef = useRef<LottieInstance | null>(null);
  const hasTrackedOnboardPvRef = useRef(false);
  const tablaCustomEvent = useTablaCustomEventAnalyticsCall();

  const trackVirtualPV = useCallback(
    (articleData: ProcessedArticleData) => {
      tablaCustomEvent({
        event: "virtual_pv",
        articleid: articleData.id,
        cue_articleid: articleData.id,
        pagination: 1,
        author:
          "authors" in articleData
            ? getAuthorsName(articleData.authors || [])
            : "",
        level2: "quick-news",
        chapter1: "",
        contentcat: 1,
        contentType: undefined,
        pubdate:
          "createdDate" in articleData
            ? formatDate(articleData.createdDate, "yyyy-MM-dd HH:mm:ss")
            : "",
        title: articleData.title,
        virtual_url: `${window.location.origin}${ONBOARD_VIRTUAL_URL}/${articleData.id}`,
      });
    },
    [tablaCustomEvent],
  );

  const setMetaTags = useCallback((card?: ProcessedArticleData) => {
    if (!card) return;
    const { metaTags } = QuickNewsMetaData({
      card,
      onBoarded: true,
      pagination: 0,
    });

    setMetaInfo(metaTags);
  }, []);

  const closeOnBoard = useCallback(() => {
    setIsAnimatingOut(true);
    setTimeout(() => {
      setOnBoarded(true);
      try {
        setPushState();
      } catch (error) {
        console.error("Failed to set onboarding cookie:", error);
      }
    }, ANIMATION_DURATION);

    // On board close set the cookie.
    Cookies.set(ONBOARD_COOKIE_NAME, "true");

    if (article) {
      setMetaTags(article);
      trackVirtualPV(article);
    }
  }, [article, setPushState, trackVirtualPV, setMetaTags]);

  // Custom hooks
  const { handleTouchStart, handleTouchEnd, handleMouseDown, handleMouseUp } =
    useDragGesture(closeOnBoard);

  // Load Lottie animations
  useEffect(() => {
    if (onBoarded || typeof window === "undefined") return;

    let mounted = true;
    let lottieLoadTimeout: ReturnType<typeof setTimeout> | undefined;

    const loadAnimations = () => {
      // Load main animation
      const isMobileCanvas = window.innerWidth < CANVAS_BREAKPOINT;
      const [canvasWidth, canvasHeight] = isMobileCanvas
        ? [CANVAS_WIDTH_MOBILE, CANVAS_HEIGHT_MOBILE]
        : [CANVAS_WIDTH_DESKTOP, CANVAS_HEIGHT_DESKTOP];
      const mainConfig: LottieConfig = {
        animationPath: `quick-news/${isMobile ? "qnWelcomeMobile.lottie" : "qnWelcome.lottie"}`,
        canvasId: "quick-news-onboarding",
        dimensions: {
          width: canvasWidth,
          height: canvasHeight,
          minHeight: canvasHeight,
        },
      };
      const mainInstance = loadLottieAnimation(mainConfig);
      if (mounted && mainInstance) {
        mainLottieInstanceRef.current = mainInstance;
        lottieLoadTimeout = setTimeout(() => {
          if (mounted) {
            setMainLottieLoaded(true);
          }
        }, LOTTIE_LOAD_DELAY);
      }
      // Load read more animation
      const readMoreConfig: LottieConfig = {
        animationPath: "quick-news/readMore.lottie",
        canvasId: "qn-onboarding-read-more",
        dimensions: {
          height: 29,
          minHeight: 29,
        },
      };
      const readMoreInstance = loadLottieAnimation(readMoreConfig);
      if (mounted && readMoreInstance) {
        readMoreLottieInstanceRef.current = readMoreInstance;
      }
    };

    loadAnimations();

    // Cleanup function
    return () => {
      mounted = false;
      if (lottieLoadTimeout) {
        clearTimeout(lottieLoadTimeout);
      }
      [mainLottieInstanceRef, readMoreLottieInstanceRef].forEach((ref) => {
        ref.current?.destroy();
        ref.current = null;
      });
    };
  }, [onBoarded, isMobile]);

  // Initialize component state
  useEffect(() => {
    const entranceTimeout = setTimeout(() => {
      setIsAnimatingIn(false);
    }, ENTRANCE_DELAY);

    return () => {
      clearTimeout(entranceTimeout);
    };
  }, []);

  // Keyboard support
  useEffect(() => {
    if (onBoarded) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (KEYBOARD_CLOSE_KEYS.has(e.key)) {
        e.preventDefault();
        closeOnBoard();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [onBoarded, closeOnBoard]);

  useEffect(() => {
    const existingCookie = Cookies.get(ONBOARD_COOKIE_NAME);
    if (existingCookie) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setOnBoarded(true);
      if (article) {
        setMetaTags(article);
        window.history.pushState(
          "article",
          article.title,
          `${ONBOARD_VIRTUAL_URL}/${article.id}${window.location.search}`,
        );
        trackVirtualPV(article);
      }
    } else {
      if (hasTrackedOnboardPvRef.current) {
        return;
      }

      hasTrackedOnboardPvRef.current = true;
      tablaCustomEvent({
        event: "virtual_pv",
        pagination: 1,
        level2: "quick-news",
        chapter1: "",
        contentcat: 1,
        contentType: undefined,
        title: "Quick News",
        virtual_url: `${window.location.origin}${ONBOARD_VIRTUAL_URL}`,
      });
    }
  }, [article, trackVirtualPV, setMetaTags, tablaCustomEvent]);

  // Don't render if user has already onboarded
  if (onBoarded) {
    return null;
  }

  return (
    <>
      <div
        className={`absolute z-50 w-full h-[100vh] bg-[linear-gradient(168deg,#272727_0%,#101010_100%)]`}
      ></div>
      <div
        className={twMerge(
          "fixed z-50 w-full h-[100vh] m-auto bg-[linear-gradient(168deg,#272727_0%,#101010_100%)] transition-all duration-300 ease-in-out",
          // Entrance animation: start from bottom
          isAnimatingIn && "top-full scale-90 opacity-0",
          // Exit animation: slide up and scale down
          isAnimatingOut && "-top-full scale-75 opacity-80",
          // Normal state: visible and centered
          !isAnimatingIn && !isAnimatingOut && "top-0 scale-100 opacity-100",
        )}
        role="dialog"
        aria-modal="true"
        aria-labelledby="onboarding-title"
        aria-describedby="onboarding-description"
      >
        <div
          data-test-id="quick-news-onboarding"
          className="grid my-auto h-full text-center"
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
          onMouseDown={handleMouseDown}
          onMouseUp={handleMouseUp}
          tabIndex={0}
          role="button"
          aria-label={
            isMobile
              ? "Swipe up or tap to start reading Quick News"
              : "Click the arrow button to start reading Quick News"
          }
        >
          <div className="w-full my-auto relative">
            <canvas
              id="quick-news-onboarding"
              ref={canvasRef}
              className={twMerge(
                "mx-auto w-full md:w-[400px] transition-opacity duration-500",
              )}
              aria-hidden="true"
            />
            {!mainLottieLoaded && (
              <img
                src={ONBOARD_IMG}
                alt="Welcome to Quick News - Tabla news in a swipeable card format"
                className="absolute inset-0 w-[280px] m-auto object-contain block"
              />
            )}
          </div>

          <div className="z-20">
            <button
              aria-label="Start reading Quick News"
              className="absolute top-[calc(100svh-65px)] left-1/2 transform -translate-x-1/2 items-center justify-center text-white focus:outline-none focus:ring-2 focus:ring-white-100 focus:ring-offset-2 focus:ring-offset-gray-900 rounded"
              onClick={closeOnBoard}
            >
              Start reading
              <canvas
                id="qn-onboarding-read-more"
                ref={readMoreCanvasRef}
                className={twMerge("mx-auto w-[11px]")}
                aria-hidden="true"
              />
            </button>
          </div>
          <QnCardNav
            canPrevFeed={false}
            canNextFeed={true}
            onPrev={NOOP}
            onNext={closeOnBoard}
          />

          {/* Screen reader only content */}
          <div className="sr-only">
            <h1 id="onboarding-title">Welcome to Quick News</h1>
            <p id="onboarding-description">
              Swipe, scroll, or use the arrow keys to view new articles.
            </p>
          </div>
        </div>
      </div>
    </>
  );
}
